import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import AdminLayout from '../../components/layout/AdminLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import {
  Plus, Edit2, Trash2, Copy, ExternalLink, Eye, ChevronDown, ChevronUp, Check,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const FRONTEND_ORIGIN = window.location.origin;

const EMPTY_BRAND = {
  slug: '',
  name: '',
  tagline: '',
  description: '',
  website_url: '',
  logo_url: '',
  primary_color: '#1D4ED8',
  accent_color: '#F97316',
  bg_color: '#0F172A',
  services: [],
  intake_questions: [],
  admin_notification_email: '',
  is_active: true,
  order: 0,
  reminders_enabled: true,
  reminder_offsets: [24, 1],
  followup_enabled: true,
  payment_links: [],
};

export default function AdminBookingBrands() {
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const [embedCopied, setEmbedCopied] = useState(null);

  const fetchBrands = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API_URL}/api/admin/booking-brands`);
      setBrands(r.data);
    } catch (e) {
      toast.error('Failed to load brands');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchBrands(); }, []);

  const handleSave = async (brand) => {
    try {
      const isNew = !brands.find((b) => b.slug === brand.slug);
      if (isNew) {
        await axios.post(`${API_URL}/api/admin/booking-brands`, brand);
      } else {
        await axios.put(`${API_URL}/api/admin/booking-brands/${brand.slug}`, brand);
      }
      toast.success(isNew ? 'Brand created' : 'Brand updated');
      setEditing(null);
      setCreating(false);
      fetchBrands();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    }
  };

  const handleDelete = async (slug) => {
    if (!window.confirm(`Delete brand "${slug}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API_URL}/api/admin/booking-brands/${slug}`);
      toast.success('Brand deleted');
      fetchBrands();
    } catch (e) {
      toast.error('Delete failed');
    }
  };

  const copyEmbed = (slug) => {
    const code = `<iframe src="${FRONTEND_ORIGIN}/embed/schedule?brand=${slug}" style="width:100%;min-height:1100px;border:0;" title="Book with David"></iframe>`;
    navigator.clipboard.writeText(code);
    setEmbedCopied(slug);
    toast.success('Embed code copied');
    setTimeout(() => setEmbedCopied(null), 2000);
  };

  return (
    <AdminLayout title="Booking Brands">
      <div className="max-w-6xl mx-auto" data-testid="admin-booking-brands-page">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-[#0A0F1C]">Booking Brands</h1>
            <p className="text-sm text-[#475569] mt-1">Manage the brands that share your central scheduler. One calendar, multiple brands.</p>
          </div>
          <Button onClick={() => { setEditing({ ...EMPTY_BRAND }); setCreating(true); }} className="btn-primary-blue" data-testid="add-brand-btn">
            <Plus size={16} className="mr-1" /> New Brand
          </Button>
        </div>

        {loading ? (
          <div className="text-center py-20"><div className="w-10 h-10 border-4 border-[#1D4ED8] border-t-transparent rounded-full animate-spin mx-auto"></div></div>
        ) : (
          <div className="space-y-3" data-testid="brands-list">
            {brands.map((b) => (
              <div key={b.slug} className="bg-white border border-[#E2E8F0] rounded-xl overflow-hidden" data-testid={`brand-row-${b.slug}`}>
                <div className="flex items-center justify-between p-4 gap-4">
                  <div className="flex items-center gap-3 min-w-0 flex-1">
                    <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: b.bg_color, color: b.accent_color }}>
                      <div className="text-xs font-bold">{b.name.charAt(0)}</div>
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <h3 className="font-bold text-[#0A0F1C] truncate">{b.name}</h3>
                        {!b.is_active && <span className="text-[10px] font-bold uppercase bg-[#F1F5F9] text-[#475569] px-2 py-0.5 rounded">Inactive</span>}
                      </div>
                      <div className="text-xs text-[#475569]">/{b.slug} · {b.services?.length || 0} services · {b.intake_questions?.length || 0} intake questions</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <button onClick={() => copyEmbed(b.slug)} className="text-xs font-semibold inline-flex items-center gap-1 px-3 py-1.5 border border-[#E2E8F0] rounded-lg hover:bg-[#F8FAFC]" data-testid={`copy-embed-${b.slug}`}>
                      {embedCopied === b.slug ? <><Check size={12} /> Copied</> : <><Copy size={12} /> Embed</>}
                    </button>
                    <a href={`/embed/schedule?brand=${b.slug}`} target="_blank" rel="noopener noreferrer" className="text-xs font-semibold inline-flex items-center gap-1 px-3 py-1.5 border border-[#E2E8F0] rounded-lg hover:bg-[#F8FAFC]" data-testid={`preview-${b.slug}`}>
                      <Eye size={12} /> Preview
                    </a>
                    <button onClick={() => setExpanded(expanded === b.slug ? null : b.slug)} className="p-1.5 hover:bg-[#F8FAFC] rounded" data-testid={`expand-${b.slug}`}>
                      {expanded === b.slug ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                    </button>
                    <button onClick={() => { setEditing({ ...b }); setCreating(false); }} className="p-1.5 hover:bg-[#F8FAFC] rounded" data-testid={`edit-${b.slug}`}>
                      <Edit2 size={14} className="text-[#1D4ED8]" />
                    </button>
                    <button onClick={() => handleDelete(b.slug)} className="p-1.5 hover:bg-rose-50 rounded" data-testid={`delete-${b.slug}`}>
                      <Trash2 size={14} className="text-rose-600" />
                    </button>
                  </div>
                </div>
                {expanded === b.slug && (
                  <div className="px-4 pb-4 border-t border-[#F1F5F9] pt-3 text-xs space-y-2">
                    <div><strong>Embed code:</strong></div>
                    <pre className="bg-[#0F172A] text-[#E2E8F0] p-3 rounded-lg overflow-x-auto text-[11px]">{`<iframe src="${FRONTEND_ORIGIN}/embed/schedule?brand=${b.slug}" style="width:100%;min-height:1100px;border:0;" title="Book with David"></iframe>`}</pre>
                    {b.website_url && <div><strong>Website:</strong> <a href={b.website_url} target="_blank" rel="noopener noreferrer" className="text-[#1D4ED8] inline-flex items-center gap-1">{b.website_url} <ExternalLink size={10} /></a></div>}
                  </div>
                )}
              </div>
            ))}
            {brands.length === 0 && (
              <div className="text-center py-12 bg-white border border-[#E2E8F0] rounded-xl text-[#475569]">No brands yet. Click "New Brand" to add one.</div>
            )}
          </div>
        )}

        {editing && (
          <BrandEditDialog
            brand={editing}
            isNew={creating}
            onClose={() => { setEditing(null); setCreating(false); }}
            onSave={handleSave}
          />
        )}
      </div>
    </AdminLayout>
  );
}

function BrandEditDialog({ brand, isNew, onClose, onSave }) {
  const [draft, setDraft] = useState(brand);
  const [uploading, setUploading] = useState(false);
  const [extracting, setExtracting] = useState(false);
  const logoInputRef = useRef(null);
  const update = (k, v) => setDraft({ ...draft, [k]: v });

  const extractColors = async () => {
    if (!draft.logo_url) { toast.error('Upload or paste a logo first'); return; }
    setExtracting(true);
    try {
      const res = await axios.post(`${API_URL}/api/admin/extract-colors`, { image_url: draft.logo_url }, { withCredentials: true });
      setDraft((d) => ({ ...d, primary_color: res.data.primary, accent_color: res.data.accent }));
      toast.success('Colors pulled from logo');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not extract colors');
    } finally {
      setExtracting(false);
    }
  };

  const slugId = (s) => (s || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '').slice(0, 24) || Math.random().toString(36).slice(2, 8);
  const addPaymentLink = () => setDraft((d) => ({ ...d, payment_links: [...(d.payment_links || []), { link_id: 'pl_' + slugId(Math.random().toString()), label: '', amount: 0, description: '', active: true }] }));
  const updatePaymentLink = (i, k, v) => setDraft((d) => { const next = [...(d.payment_links || [])]; next[i] = { ...next[i], [k]: v }; return { ...d, payment_links: next }; });
  const removePaymentLink = (i) => setDraft((d) => ({ ...d, payment_links: (d.payment_links || []).filter((_, idx) => idx !== i) }));
  const copyLink = (url) => { navigator.clipboard.writeText(url); toast.success('Link copied'); };

  const uploadLogo = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API_URL}/api/admin/site/upload-image`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      const abs = res.data.url.startsWith('http') ? res.data.url : `${API_URL}${res.data.url}`;
      setDraft((d) => ({ ...d, logo_url: abs }));
      toast.success('Logo uploaded');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Logo upload failed');
    } finally {
      setUploading(false);
    }
  };

  const updateService = (i, k, v) => {
    const next = [...draft.services];
    next[i] = { ...next[i], [k]: v };
    setDraft({ ...draft, services: next });
  };
  const addService = () => {
    setDraft({
      ...draft,
      services: [...(draft.services || []), { service_id: `svc_${Math.random().toString(36).slice(2, 12)}`, name: 'New Session', duration_min: 30, price: 0, is_featured: false, description: '', cta_text: 'Book Session' }],
    });
  };
  const removeService = (i) => {
    setDraft({ ...draft, services: draft.services.filter((_, idx) => idx !== i) });
  };

  const updateQuestion = (i, k, v) => {
    const next = [...draft.intake_questions];
    next[i] = { ...next[i], [k]: v };
    setDraft({ ...draft, intake_questions: next });
  };
  const addQuestion = () => {
    setDraft({
      ...draft,
      intake_questions: [...(draft.intake_questions || []), { key: `q_${Math.random().toString(36).slice(2, 6)}`, label: 'New question', type: 'text', placeholder: '', min_len: 10, required: true }],
    });
  };
  const removeQuestion = (i) => {
    setDraft({ ...draft, intake_questions: draft.intake_questions.filter((_, idx) => idx !== i) });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/50 p-4 overflow-y-auto" data-testid="brand-edit-dialog">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl my-8">
        <div className="px-6 py-4 border-b border-[#E2E8F0] flex items-center justify-between">
          <h2 className="font-bold text-lg text-[#0A0F1C]">{isNew ? 'Create' : 'Edit'} Brand</h2>
          <button onClick={onClose} className="text-sm text-[#475569] hover:underline" data-testid="close-dialog">Cancel</button>
        </div>
        <div className="px-6 py-5 space-y-5 max-h-[70vh] overflow-y-auto">
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Slug (URL key)" required>
              <Input value={draft.slug} disabled={!isNew} onChange={(e) => update('slug', e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))} placeholder="e.g. purposefit" data-testid="field-slug" />
            </Field>
            <Field label="Brand Name" required>
              <Input value={draft.name} onChange={(e) => update('name', e.target.value)} data-testid="field-name" />
            </Field>
            <Field label="Tagline" className="sm:col-span-2">
              <Input value={draft.tagline} onChange={(e) => update('tagline', e.target.value)} data-testid="field-tagline" />
            </Field>
            <Field label="Description" className="sm:col-span-2">
              <Textarea value={draft.description} rows={2} onChange={(e) => update('description', e.target.value)} data-testid="field-description" />
            </Field>
            <Field label="Website URL"><Input value={draft.website_url || ''} onChange={(e) => update('website_url', e.target.value)} placeholder="https://example.com" data-testid="field-website" /></Field>
            <Field label="Brand Logo (header of emails + scheduler)" className="sm:col-span-2">
              <div className="flex gap-2 items-center">
                <Input value={draft.logo_url || ''} onChange={(e) => update('logo_url', e.target.value)} placeholder="Paste a URL or click Upload →" data-testid="field-logo" />
                <input ref={logoInputRef} type="file" accept="image/*" className="hidden" onChange={(e) => uploadLogo(e.target.files?.[0])} data-testid="field-logo-file" />
                <Button type="button" onClick={() => logoInputRef.current?.click()} disabled={uploading} className="btn-outline whitespace-nowrap" data-testid="upload-logo-btn">{uploading ? 'Uploading…' : 'Upload'}</Button>
              </div>
              {draft.logo_url && <img src={draft.logo_url} alt="logo preview" className="h-12 mt-2 object-contain" data-testid="logo-preview" />}
            </Field>
            <div className="sm:col-span-2 -mt-2">
              <Button type="button" onClick={extractColors} disabled={extracting} className="btn-outline text-xs" data-testid="extract-colors-btn">
                {extracting ? 'Reading logo…' : '🎨 Pull primary + secondary colors from logo'}
              </Button>
            </div>
            <Field label="Primary Color (headers / buttons / links)"><Input type="color" value={draft.primary_color} onChange={(e) => update('primary_color', e.target.value)} data-testid="field-primary" /></Field>
            <Field label="Secondary Color (accents)"><Input type="color" value={draft.accent_color} onChange={(e) => update('accent_color', e.target.value)} data-testid="field-accent" /></Field>
            <Field label="Email Footer Background"><Input type="color" value={draft.bg_color} onChange={(e) => update('bg_color', e.target.value)} data-testid="field-bg" /></Field>
            <Field label="Brand Notification Email (gets every booking)" className="sm:col-span-2">
              <Input value={draft.admin_notification_email || ''} onChange={(e) => update('admin_notification_email', e.target.value)} placeholder="bookings@yourbrand.com (DCA + David's inbox always get a copy too)" data-testid="field-admin-email" />
            </Field>
            <Field label="Display Order"><Input type="number" value={draft.order} onChange={(e) => update('order', parseInt(e.target.value || 0))} data-testid="field-order" /></Field>
            <Field label="Active">
              <label className="flex items-center gap-2 text-sm cursor-pointer mt-2">
                <input type="checkbox" checked={draft.is_active} onChange={(e) => update('is_active', e.target.checked)} data-testid="field-active" />
                Brand is live
              </label>
            </Field>
          </div>

          {/* Automatic Reminders */}
          <div className="border-t border-[#E2E8F0] pt-4">
            <h3 className="font-bold text-[#0A0F1C] mb-1">Automatic Reminders</h3>
            <p className="text-xs text-[#475569] mb-3">Send branded email (and SMS if Twilio is set) reminders before each session. DCA + David always get a copy of new bookings; reminders go to the client.</p>
            <label className="flex items-center gap-2 text-sm cursor-pointer mb-3">
              <input type="checkbox" checked={draft.reminders_enabled !== false} onChange={(e) => update('reminders_enabled', e.target.checked)} data-testid="field-reminders-enabled" />
              Send automatic reminders
            </label>
            {draft.reminders_enabled !== false && (
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="First reminder (hours before)">
                  <Input type="number" min="1" value={(draft.reminder_offsets || [24, 1])[0]} onChange={(e) => update('reminder_offsets', [parseInt(e.target.value || 0), (draft.reminder_offsets || [24, 1])[1]])} data-testid="field-reminder-offset-1" />
                </Field>
                <Field label="Second reminder (hours before)">
                  <Input type="number" min="0" value={(draft.reminder_offsets || [24, 1])[1]} onChange={(e) => update('reminder_offsets', [(draft.reminder_offsets || [24, 1])[0], parseInt(e.target.value || 0)])} data-testid="field-reminder-offset-2" />
                </Field>
              </div>
            )}
            <label className="flex items-center gap-2 text-sm cursor-pointer mt-3">
              <input type="checkbox" checked={draft.followup_enabled !== false} onChange={(e) => update('followup_enabled', e.target.checked)} data-testid="field-followup-enabled" />
              Send a no-show / post-session follow-up email the next day
            </label>
          </div>

          {/* Payment Links (custom charges → direct Stripe URLs) */}
          <div className="border-t border-[#E2E8F0] pt-4">
            <div className="flex items-center justify-between mb-1">
              <h3 className="font-bold text-[#0A0F1C]">Payment Links</h3>
              <div className="flex items-center gap-2">
                {!isNew && (draft.payment_links || []).some((p) => p.amount > 0) && (
                  <Button onClick={() => { const all = (draft.payment_links || []).filter((p) => p.amount > 0).map((p) => `${p.label || 'Payment'} ($${p.amount}): ${API_URL}/api/pay/link/${draft.slug}/${p.link_id}`).join('\n'); navigator.clipboard.writeText(all); toast.success('All links copied'); }} className="btn-outline text-xs" data-testid="copy-all-links-btn"><Copy size={12} className="mr-1" /> Copy all</Button>
                )}
                <Button onClick={addPaymentLink} className="btn-primary-blue text-xs" data-testid="add-payment-link-btn"><Plus size={12} className="mr-1" /> Add Link</Button>
              </div>
            </div>
            <p className="text-xs text-[#475569] mb-3">Create a charge (label + amount). Each gets a direct one-click Stripe checkout URL you can paste anywhere. Receipts are this brand's branding; DCA + David are copied.</p>
            <div className="space-y-3">
              {(draft.payment_links || []).length === 0 && <p className="text-xs text-[#94A3B8]">No payment links yet.</p>}
              {(draft.payment_links || []).map((pl, i) => {
                const url = `${API_URL}/api/pay/link/${draft.slug}/${pl.link_id}`;
                return (
                  <div key={pl.link_id} className="border border-[#E2E8F0] rounded-lg p-3 space-y-2" data-testid={`payment-link-${i}`}>
                    <div className="grid sm:grid-cols-12 gap-2">
                      <Input className="sm:col-span-6" placeholder="Label (e.g. 6-Month Program)" value={pl.label} onChange={(e) => updatePaymentLink(i, 'label', e.target.value)} data-testid={`pl-label-${i}`} />
                      <Input className="sm:col-span-3" type="number" min="1" placeholder="Amount $" value={pl.amount} onChange={(e) => updatePaymentLink(i, 'amount', parseFloat(e.target.value || 0))} data-testid={`pl-amount-${i}`} />
                      <label className="sm:col-span-2 flex items-center gap-1 text-xs"><input type="checkbox" checked={pl.active !== false} onChange={(e) => updatePaymentLink(i, 'active', e.target.checked)} /> Active</label>
                      <button onClick={() => removePaymentLink(i)} className="sm:col-span-1 text-red-500 hover:text-red-700 flex items-center justify-center" data-testid={`pl-remove-${i}`}><Trash2 size={14} /></button>
                    </div>
                    <Input placeholder="Short description (optional)" value={pl.description || ''} onChange={(e) => updatePaymentLink(i, 'description', e.target.value)} />
                    {!isNew && draft.slug && pl.amount > 0 && (
                      <div className="flex items-center gap-3 bg-[#F8FAFC] rounded px-2 py-1.5">
                        <img src={`https://api.qrserver.com/v1/create-qr-code/?size=120x120&margin=4&data=${encodeURIComponent(url)}`} alt="QR" className="w-14 h-14 rounded bg-white p-0.5 border border-[#E2E8F0]" data-testid={`pl-qr-${i}`} />
                        <code className="text-[11px] text-[#475569] flex-1 truncate" data-testid={`pl-url-${i}`}>{url}</code>
                        <button onClick={() => copyLink(url)} className="text-[#1D4ED8] hover:text-[#1e40af]" title="Copy" data-testid={`pl-copy-${i}`}><Copy size={13} /></button>
                        <a href={url} target="_blank" rel="noopener noreferrer" className="text-[#1D4ED8] hover:text-[#1e40af]" title="Open"><ExternalLink size={13} /></a>
                      </div>
                    )}
                    {isNew && <p className="text-[11px] text-[#94A3B8]">Save the brand first to generate the shareable URL.</p>}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Services */}
          <div className="border-t border-[#E2E8F0] pt-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-bold text-[#0A0F1C]">Services</h3>
              <Button onClick={addService} className="btn-primary-blue text-xs" data-testid="add-service-btn"><Plus size={12} className="mr-1" /> Add Service</Button>
            </div>
            <div className="space-y-2">
              {draft.services?.map((s, i) => (
                <div key={i} className="bg-[#F8FAFC] border border-[#E2E8F0] rounded-lg p-3" data-testid={`service-row-${i}`}>
                  <div className="grid sm:grid-cols-2 gap-2">
                    <Input placeholder="Name" value={s.name} onChange={(e) => updateService(i, 'name', e.target.value)} />
                    <Input placeholder="Duration (min)" type="number" value={s.duration_min} onChange={(e) => updateService(i, 'duration_min', parseInt(e.target.value || 0))} />
                    <Input placeholder="Price" type="number" step="0.01" value={s.price} onChange={(e) => updateService(i, 'price', parseFloat(e.target.value || 0))} />
                    <Input placeholder="CTA text" value={s.cta_text || ''} onChange={(e) => updateService(i, 'cta_text', e.target.value)} />
                    <Textarea placeholder="Short description" rows={2} value={s.description} onChange={(e) => updateService(i, 'description', e.target.value)} className="sm:col-span-2" />
                  </div>
                  <div className="flex items-center justify-between mt-2">
                    <label className="text-xs inline-flex items-center gap-1"><input type="checkbox" checked={s.is_featured} onChange={(e) => updateService(i, 'is_featured', e.target.checked)} /> Featured</label>
                    <button onClick={() => removeService(i)} className="text-xs text-rose-600 hover:underline" data-testid={`remove-service-${i}`}>Remove</button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Intake Questions */}
          <div className="border-t border-[#E2E8F0] pt-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-bold text-[#0A0F1C]">Intake Questions</h3>
              <Button onClick={addQuestion} className="btn-primary-blue text-xs" data-testid="add-question-btn"><Plus size={12} className="mr-1" /> Add Question</Button>
            </div>
            <div className="space-y-2">
              {draft.intake_questions?.map((q, i) => (
                <div key={i} className="bg-[#F8FAFC] border border-[#E2E8F0] rounded-lg p-3" data-testid={`question-row-${i}`}>
                  <div className="grid sm:grid-cols-2 gap-2">
                    <Input placeholder="Key (e.g. career_stage)" value={q.key} onChange={(e) => updateQuestion(i, 'key', e.target.value.replace(/[^a-z0-9_]/g, ''))} />
                    <select className="w-full px-3 py-2 border border-[#E2E8F0] rounded-md text-sm" value={q.type} onChange={(e) => updateQuestion(i, 'type', e.target.value)}>
                      <option value="text">Text</option>
                      <option value="select">Multiple Choice</option>
                    </select>
                    <Textarea placeholder="Question label" rows={2} value={q.label} onChange={(e) => updateQuestion(i, 'label', e.target.value)} className="sm:col-span-2" />
                    {q.type === 'text' && (
                      <>
                        <Input placeholder="Placeholder" value={q.placeholder || ''} onChange={(e) => updateQuestion(i, 'placeholder', e.target.value)} />
                        <Input placeholder="Min length" type="number" value={q.min_len || 10} onChange={(e) => updateQuestion(i, 'min_len', parseInt(e.target.value || 0))} />
                      </>
                    )}
                    {q.type === 'select' && (
                      <Textarea placeholder="Options (one per line)" rows={3} value={(q.options || []).join('\n')} onChange={(e) => updateQuestion(i, 'options', e.target.value.split('\n').filter(Boolean))} className="sm:col-span-2" />
                    )}
                  </div>
                  <div className="flex items-center justify-between mt-2">
                    <label className="text-xs inline-flex items-center gap-1"><input type="checkbox" checked={q.required !== false} onChange={(e) => updateQuestion(i, 'required', e.target.checked)} /> Required</label>
                    <button onClick={() => removeQuestion(i)} className="text-xs text-rose-600 hover:underline">Remove</button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="px-6 py-4 border-t border-[#E2E8F0] flex items-center justify-end gap-3">
          <Button onClick={onClose} variant="outline" data-testid="cancel-btn">Cancel</Button>
          <Button onClick={() => onSave(draft)} className="btn-primary-blue" data-testid="save-brand-btn">Save Brand</Button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, required, children, className = '' }) {
  return (
    <div className={className}>
      <label className="block text-xs font-bold text-[#0A0F1C] uppercase tracking-wider mb-1">{label}{required && ' *'}</label>
      {children}
    </div>
  );
}
